import styled from '@emotion/styled';

import { round } from '../../../../lib/math';
import { FlowToken } from '../../flow-arguments/FlowToken';

import { useI18n } from '../../../../hooks/useI18nFormatters';

import { scrollbars } from '../../../../theme/elements/scrollbars';
import { suaf } from '../../../../theme/functions/su';
import { glyph } from '../../../../theme/glyph';
import { theme } from '../../../../theme/theme';

import { Glyph } from '../../../../components/common/Glyph';
import { TextSmall } from '../../../../components/common/text/TextSmall';
import { TextSmallMedium } from '../../../../components/common/text/TextSmallMedium';

import { executionStateType } from './executionStateType';

export function CardResult(props) {
  const { i18n } = useI18n();

  let tokens = null;
  let title = null;
  let subtitle = null;
  let icon = null;

  function getExecutionTime() {
    if (props.executionState.elapsedTime < 100) {
      return `${round(props.executionState.elapsedTime ?? 0, 0)}ms`;
    }

    return `${round((props.executionState.elapsedTime ?? 0) / 1000, 1)}s`;
  }

  switch (props.executionState.type) {
    case executionStateType.error:
      icon = (
        <Glyph
          source={glyph.core.exclamationmarkTriangleFill}
          color={theme.color.red}
          size={theme.icon.size_default}
        />
      );
      tokens = [
        {
          key: '__error__',
          type: 'error',
          title: 'Error',
          value: props.executionState.tokens?.[`card::${props.nodeId}::error`],
        },
      ];

      title = i18n.messageFormatter('flow.cardErrorAfter', { time: getExecutionTime() });
      subtitle = i18n.messageFormatter('flow.errorTagGenerated');
      break;
    case executionStateType.true:
      icon = (
        <Glyph
          source={glyph.core.checkmarkCircle}
          color={theme.color.blue}
          size={theme.icon.size_default}
        />
      );
      //value = props.executionState.tokens?.[`card::${props.nodeId}::error`];
      title = i18n.messageFormatter('flow.cardFinishedIn', { time: getExecutionTime() });
      subtitle = i18n.messageFormatter('flow.cardFinishedInTrueHint');

      break;
    case executionStateType.false:
      icon = (
        <Glyph
          source={glyph.core.crossCircle}
          color={theme.color.yellow}
          size={theme.icon.size_default}
        />
      );
      //value = props.executionState.tokens?.[`card::${props.nodeId}::error`];
      title = i18n.messageFormatter('flow.cardFinishedIn', { time: getExecutionTime() });
      subtitle = i18n.messageFormatter('flow.cardFinishedInFalseHint');
      break;
    case executionStateType.end:
      icon = (
        <Glyph
          source={glyph.core.checkmarkCircle}
          color={theme.color.blue}
          size={theme.icon.size_default}
        />
      );
      //value = props.executionState.tokens?.[`card::${props.nodeId}::error`];
      title = i18n.messageFormatter('flow.cardFinishedIn', { time: getExecutionTime() });
      subtitle = i18n.messageFormatter('flow.cardFinishedInHint');

      if (props.card?.tokens) {
        tokens =
          props.card?.tokens.map((token) => {
            let value = props.executionState.tokens?.[`action::${props.nodeId}::${token.id}`];

            if (token.type === 'boolean') {
              value =
                value === true
                  ? i18n.messageFormatter('common.true')
                  : i18n.messageFormatter('common.false');
            }

            return {
              key: token.id,
              type: token.type,
              title: token.title,
              // The image tokens are serialized so there is no url to show. This should be fixed at
              // some point.
              value: token.type === 'image' ? 'Preview not available.' : value,
            };
          }) ?? null;

        if (tokens?.length === 1) {
          subtitle = i18n.messageFormatter('flow.cardFinishedInOneTagHint');
        } else if (tokens?.length > 1) {
          subtitle = i18n.messageFormatter('flow.cardFinishedInMultipleTagsHint');
        }
      }

      break;
    default:
      console.log(JSON.stringify(props.executionState.result, null, 2));
  }

  return (
    <CardResult.Root>
      <CardResult.Header>
        <CardResult.HeaderIcon>{icon}</CardResult.HeaderIcon>
        <CardResult.HeaderContent>
          <TextSmallMedium>{title}</TextSmallMedium>
          <TextSmall color={theme.color.text_light}>{subtitle}</TextSmall>
        </CardResult.HeaderContent>
      </CardResult.Header>

      {tokens != null && (
        <>
          <CardResult.DividerWrapper>
            <CardResult.Divider />
          </CardResult.DividerWrapper>

          <CardResult.Content>
            {tokens.map((token, index) => {
              return (
                <CardResult.TokenResult key={token.key}>
                  <CardResult.TokenWrapper>
                    <FlowToken isHoverable={false} token={token} label={token.title} />
                  </CardResult.TokenWrapper>
                  <CardResult.Value>{token.value}</CardResult.Value>
                </CardResult.TokenResult>
              );
            })}
          </CardResult.Content>
        </>
      )}
    </CardResult.Root>
  );
}

CardResult.Root = styled.div`
  ${scrollbars.dark};
  // Used in src/containers/flows/flow-arguments/FlowToken.js
  --flow-token-max-width: 250px;

  overflow-y: auto;
  border-radius: ${theme.borderRadius.default};
  width: 300px;
  max-height: 400px;
  padding: ${suaf(2)};
`;

CardResult.Header = styled.div`
  display: flex;
`;

CardResult.HeaderContent = styled.div``;

CardResult.HeaderIcon = styled.div`
  padding-inline-end: ${suaf(1)};
`;

CardResult.Content = styled.div`
  display: flex;
  flex-direction: column;
`;

CardResult.DividerWrapper = styled.div`
  padding-top: ${suaf(1)};
`;

CardResult.Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.color.line};
`;

CardResult.TokenResult = styled.div`
  padding-top: ${suaf(1)};
`;

CardResult.TokenWrapper = styled.div`
  padding-bottom: ${suaf(1)};
`;

CardResult.Value = styled.div`
  ${scrollbars.dark};
  max-height: 120px;
  padding: ${suaf(1)};
  border-radius: ${theme.borderRadius.small};
  background-color: ${theme.color.body};
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
`;
